import { type JSX, useEffect, useMemo, useRef, useState } from 'react'

import { useTranslation } from '@molecule/app-react'

import type { PostSection } from '../content-types.js'
import { ProvenanceToggle } from './ProvenanceToggle.js'

/**
 * The phone form of the gutter (§5): a bar fixed to the bottom carrying the
 * SAME two switches as the desktop header (which is not rendered at phone
 * width, so the toggles exist in exactly one place), and a panel that slides up
 * showing the IN-VIEW section's summary, prompt, or both.
 *
 * TRACKING THE SECTION IN VIEW. The panel must follow the reader down the
 * article, and three things make that work:
 *
 *   1. The observer watches the ROWS (a section's heading together with its
 *      paragraphs), not the headings alone. A heading is only ~40px tall, so
 *      with a heading-only observer nothing intersects the moment the reader
 *      scrolls past it and the panel freezes on a previous section.
 *   2. The chosen section is decided from the rows' POSITIONS — the last one
 *      whose top has passed the reading line — with the observer only prompting
 *      a recompute. Deciding from positions is what makes a JUMP land
 *      correctly: scrolling straight to the bottom skips every heading in
 *      between, and an observer that remembered "the last one I saw" would show
 *      the wrong section.
 *   3. The bind is RETRIED on the next frame while the rows are missing. They
 *      belong to the article, which commits after this component, so a
 *      one-shot query on mount can find nothing and leave the panel frozen.
 *
 * A section with neither a summary nor a prompt closes the panel; the bar
 * stays. The panel is at most ~45% of the viewport and scrolls inside itself,
 * and the article carries bottom padding so its last paragraph can be scrolled
 * clear of the bar.
 */
export function MobileGutter({
  sections,
  showSummary,
  showPrompts,
  onToggleSummary,
  onTogglePrompts,
}: {
  sections: PostSection[]
  showSummary: boolean
  showPrompts: boolean
  onToggleSummary: (next: boolean) => void
  onTogglePrompts: (next: boolean) => void
}): JSX.Element {
  const { t } = useTranslation()
  const [activeId, setActiveId] = useState<string>(sections[0]?.id ?? '')
  const activeRef = useRef(activeId)

  /** The ids the tracking watches, in document order. */
  const ids = useMemo(() => sections.map((section) => section.id), [sections])

  useEffect(() => {
    if (ids.length === 0) return
    let observer: IntersectionObserver | null = null
    let update: (() => void) | null = null
    let frame = 0
    let stop = false

    /** Bind the tracking once the section rows are in the DOM. */
    const bind = (): void => {
      if (stop) return
      const rows = ids
        .map((id) => document.querySelector(`[data-mol-id="row-${id}"]`))
        .filter((el): el is HTMLElement => el !== null)
      if (rows.length === 0) {
        // The article has not committed yet — try again next frame.
        frame = requestAnimationFrame(bind)
        return
      }

      /**
       * The section the reader is in: the LAST row whose top has passed the
       * READING LINE — a third of the way down the viewport, where a reader's
       * eye sits. Computed from POSITIONS, so a jump to the bottom lands on the
       * section actually passed rather than the last one an event saw.
       */
      const pick = (): string => {
        const line = window.innerHeight * 0.34
        let best = rows[0]!
        for (const row of rows) {
          if (row.getBoundingClientRect().top <= line) best = row
        }
        return (best.getAttribute('data-mol-id') ?? '').replace(/^row-/, '')
      }

      const recount = (): void => {
        const next = pick()
        if (next !== activeRef.current) {
          activeRef.current = next
          setActiveId(next)
        }
      }
      update = recount

      // The observer fires while a boundary crosses the band; the scroll
      // listener recomputes from POSITIONS on every movement — that is what
      // keeps the panel following the reader through a section that fills the
      // whole screen, where no boundary ever crosses anything.
      observer = new IntersectionObserver(recount, {
        rootMargin: '-10% 0px -55% 0px',
        threshold: [0, 1],
      })
      for (const row of rows) observer.observe(row)

      window.addEventListener('scroll', recount, { passive: true })
      window.addEventListener('resize', recount)

      // Seed from POSITIONS (never from an observer event): a page loaded
      // deep-linked, or restored mid-article, shows the right section at once.
      recount()
    }

    bind()
    return () => {
      stop = true
      cancelAnimationFrame(frame)
      observer?.disconnect()
      if (update) {
        window.removeEventListener('scroll', update)
        window.removeEventListener('resize', update)
      }
    }
  }, [ids])

  const active = sections.find((section) => section.id === activeId)
  const summary = showSummary ? (active?.summary ?? '') : ''
  const promptParagraph = showPrompts
    ? active?.paragraphs.find((p) => p.origin === 'ai' && p.prompt)
    : undefined
  const open = Boolean(summary || promptParagraph)

  return (
    <div className="mol-phone-gutter" data-mol-id="phone-gutter">
      {open && (
        <div
          className="mol-phone-panel"
          data-mol-id="phone-panel"
          role="region"
          aria-label={t('post.gutterRegion', undefined, { defaultValue: 'Section commentary' })}
        >
          {summary && (
            <div className="mol-summary" data-mol-id="phone-summary">
              <p className="mol-gutter-label">
                {t('post.summaryLabel', undefined, { defaultValue: 'Summary' })}
              </p>
              <p className="mol-summary-text">{summary}</p>
            </div>
          )}
          {promptParagraph?.prompt && (
            <div className="mol-prompt" data-mol-id="phone-prompt">
              <p className="mol-gutter-label">
                {t('post.promptLabel', undefined, { defaultValue: 'Prompt' })}
                {promptParagraph.model && (
                  <span className="mol-prompt-model"> · {promptParagraph.model}</span>
                )}
              </p>
              <p className="mol-prompt-text">{promptParagraph.prompt}</p>
            </div>
          )}
        </div>
      )}
      <div className="mol-phone-bar" data-mol-id="phone-bar">
        <ProvenanceToggle
          id="phone-summary-toggle"
          label={t('post.summaries', undefined, { defaultValue: 'Summary' })}
          checked={showSummary}
          onChange={onToggleSummary}
        />
        <ProvenanceToggle
          id="phone-prompts-toggle"
          label={t('post.prompts', undefined, { defaultValue: 'Prompt' })}
          checked={showPrompts}
          onChange={onTogglePrompts}
        />
      </div>
    </div>
  )
}
